import React, { useState } from "react";
import { X } from "lucide-react";
import { cn } from "../../../lib/utils";
import { formatCurrency } from "../../../utils/formatters";
import { Input } from "../../ui/input";
import { NumberInput } from "../../ui/number-input";

/**
 * Sub-row for a single component (material / operation / hardware) inside a
 * Custom Mode assembly.
 *
 * Columns used: type badge | item / operation | quantity or hours | unit cost |
 * amount | remove. Custom line dimensions live above these rows as reference
 * information, so repeating three empty L/W/H columns here only hid actions.
 *
 * Because this renders as a normal <tr> under the same <colgroup> as the
 * parent row, every cell lives under its semantic parent column header —
 * by construction, no alignment math required.
 */
export default function ComponentSubRow({
  component,
  lineIndex,
  componentIndex,
  lineQuantity,
  dispatch,
  availableMaterials = [],
  selectedLaborRate,
}) {
  const { type } = component;

  const update = (field, value) => {
    dispatch({
      type: "UPDATE_COMPONENT",
      index: lineIndex,
      componentIndex,
      field,
      value,
    });
  };

  const handleRemove = () => {
    dispatch({ type: "REMOVE_COMPONENT", index: lineIndex, componentIndex });
  };

  const handleCatalogSelect = (e) => {
    const id = e.target.value ? parseInt(e.target.value, 10) : null;
    update("material_id", id);
    if (id) {
      const mat = availableMaterials.find((m) => m.id === id);
      if (mat) {
        update("name", mat.name);
        // Auto-fill sheet dimensions + unit cost from the catalog entry when
        // the material has them. User can still override any field by typing.
        const catalogDims = {};
        if (mat.sheet_length_in != null)
          catalogDims.length = Number(mat.sheet_length_in);
        if (mat.sheet_width_in != null)
          catalogDims.width = Number(mat.sheet_width_in);
        if (Object.keys(catalogDims).length > 0) {
          update("dimensions", {
            ...(component.dimensions || {}),
            ...catalogDims,
          });
        }
        if (mat.cost_per_sheet != null) {
          update("unit_cost", Number(mat.cost_per_sheet));
        }
      }
    } else {
      // Clearing the catalog pick must not wipe a name the estimator typed —
      // only the auto-filled one goes with it. A blank name is what made
      // custom lines fail validation and price at $0 in silence.
      const previous = availableMaterials.find(
        (m) => m.id === component.material_id,
      );
      if (previous && previous.name === component.name) {
        update("name", "");
      }
    }
  };

  // Compute costs — materials/hardware use user-entered unit_cost,
  // operations derive cost from hours × the active labor rate.
  const laborRate = parseFloat(selectedLaborRate) || 0;
  const opUnitCost =
    type === "operation" && laborRate > 0
      ? (component.hours_per_unit || 0) * laborRate
      : null;

  const extCost =
    type === "material" || type === "hardware"
      ? (component.unit_cost || 0) *
        (component.quantity_per_unit || 1) *
        (lineQuantity || 1)
      : type === "operation" && opUnitCost != null
        ? opUnitCost * (lineQuantity || 1)
        : null;

  const badgeLabel =
    type === "material"
      ? "Material"
      : type === "operation"
        ? "Operation"
        : "Purchased";

  const badgeClass =
    type === "operation"
      ? "bg-violet-500/10 text-violet-300 border-violet-500/30"
      : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";

  return (
    <>
      <tr className="border-b border-estimate-border/40 text-[12px] shadow-[inset_3px_0_0_0_theme(colors.custom.DEFAULT)] bg-amber-500/[0.02]">
        {/* # — type badge */}
        <td className="py-1.5 px-2 text-center align-middle">
          <span
            className={cn(
              "inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider border whitespace-nowrap",
              badgeClass,
            )}
          >
            {badgeLabel}
          </span>
        </td>

        {/* Product — name + inline unit/dept selector */}
        <td className="py-1.5 px-2 align-middle">
          <div className="flex items-start gap-1.5 min-w-0">
            {/* A material row carries BOTH controls. The catalog select is a
                shortcut that fills the name; the name is what the component is
                actually identified by, and a component with no name fails
                backend validation — which used to leave the line at $0.00 with
                nowhere to type a name. Operation and Hardware rows always had
                this input; materials were the odd one out. */}
            {type === "material" ? (
              <div className="flex flex-1 min-w-0 flex-col gap-1">
                {availableMaterials.length > 0 ? (
                  <select
                    value={component.material_id || ""}
                    onChange={handleCatalogSelect}
                    aria-label="Catalog material"
                    className="w-full min-w-0 h-8 px-2 text-[12px] rounded-md border border-[var(--input-border-color)] bg-[var(--input-bg-color)] text-[var(--input-text-color)]"
                  >
                    <option value="">-- Select material --</option>
                    {availableMaterials.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                ) : null}
                <Input
                  value={component.name || ""}
                  onChange={(e) => update("name", e.target.value)}
                  aria-label="Material name"
                  placeholder="Material name (required)"
                  aria-invalid={!String(component.name || "").trim()}
                  className={cn(
                    "w-full min-w-0 h-8 text-[12px] px-2",
                    !String(component.name || "").trim() &&
                      "border-rose-500/60 focus-visible:ring-rose-500/40",
                  )}
                />
              </div>
            ) : (
              <Input
                value={component.name || ""}
                onChange={(e) => update("name", e.target.value)}
                placeholder={
                  type === "operation"
                    ? "e.g. CNC Profile, Laminate"
                    : "e.g. Stainless Screws"
                }
                className="flex-1 min-w-0 h-8 text-[12px] px-2"
              />
            )}

            {type === "material" && (
              <select
                value={component.unit || "each"}
                onChange={(e) => update("unit", e.target.value)}
                title="Pricing basis — how this material is sold and priced"
                className="shrink-0 h-8 px-2 text-[11px] rounded-md border border-[var(--input-border-color)] bg-[var(--input-bg-color)] text-[var(--input-text-color)]"
              >
                <option value="each">per Each</option>
                <option value="lineal_foot">per Lin Ft</option>
                <option value="sqft">per Sq Ft</option>
                <option value="sheet">per Sheet</option>
                <option value="board">per Board</option>
                <option value="gallon">per Gallon</option>
                <option value="pound">per Lb</option>
              </select>
            )}

            {type === "operation" && (
              <select
                value={component.department || "fab"}
                onChange={(e) => update("department", e.target.value)}
                title="Department performing this operation"
                className="shrink-0 h-8 px-2 text-[11px] rounded-md border border-[var(--input-border-color)] bg-[var(--input-bg-color)] text-[var(--input-text-color)]"
              >
                <option value="cnc">in CNC</option>
                <option value="cut">in Cut</option>
                <option value="fab">in Fab</option>
                <option value="finish">in Finish</option>
              </select>
            )}
          </div>
        </td>

        {/* Qty — qty-per-unit, or operation hours in the chosen basis */}
        <td className="py-1.5 px-1 text-center align-middle">
          {type === "operation" ? (
            <OperationHoursCell
              hoursPerUnit={component.hours_per_unit}
              lineQuantity={lineQuantity}
              onChangeHoursPerUnit={(v) => update("hours_per_unit", v)}
            />
          ) : (
            <NumberInput
              align="center"
              step="1"
              min="0"
              value={component.quantity_per_unit ?? 1}
              onValueChange={(v) => update("quantity_per_unit", v ?? 1)}
              title="Quantity per assembly unit"
              className="h-8 text-[12px] px-1 w-full max-w-[60px] mx-auto"
            />
          )}
        </td>

        {/* Unit Cost — editable for materials/hardware, derived for operations */}
        <td className="py-1.5 px-2 text-right align-middle">
          {type !== "operation" ? (
            <NumberInput
              align="right"
              step="0.01"
              min="0"
              value={component.unit_cost}
              onValueChange={(v) => update("unit_cost", v ?? 0)}
              title="Cost per unit"
              placeholder="0"
              className="h-8 text-[12px] px-1 w-full max-w-[84px] ml-auto"
            />
          ) : opUnitCost != null ? (
            <span
              className="text-violet-400 text-[12px] whitespace-nowrap"
              title={`${component.hours_per_unit || 0} hrs × $${laborRate.toFixed(2)}/hr`}
            >
              {formatCurrency(opUnitCost)}
            </span>
          ) : (
            <span className="text-estimate-muted">—</span>
          )}
        </td>

        {/* Amount — extended cost */}
        <td
          className={cn(
            "py-1.5 px-2 text-right align-middle font-medium whitespace-nowrap",
            type === "operation" ? "text-violet-400" : "text-emerald-400",
          )}
        >
          {extCost != null ? formatCurrency(extCost) : "—"}
        </td>

        {/* Actions — remove */}
        <td className="py-1.5 px-2 text-center align-middle">
          <button
            type="button"
            onClick={handleRemove}
            title="Remove component"
            className="wb-plain text-estimate-muted hover:text-rose-500 transition-colors p-0.5 rounded"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>

      {/* Rip cut fields — full-width sub-sub-row, only when unit is sheet */}
      {type === "material" && component.unit === "sheet" && (
        <RipCutRow
          component={component}
          update={update}
          lineQuantity={lineQuantity}
        />
      )}
    </>
  );
}

/* ── Operation hours: per-unit vs line-total ──────────────────────────────
 * The stored field is always `hours_per_unit` — one assembly unit's hours —
 * because that is what the costing engine multiplies. But an estimator times
 * the WHOLE line ("3 hours to laminate all 11 panels"), and typing that
 * per-unit means typing 0.272727…, which is both painful and lossy.
 *
 * So the input has a basis toggle. Line-total divides by the line quantity on
 * the way in; the other figure is always displayed underneath, so nobody has
 * to guess which number the box is holding.
 */

/** hours_per_unit implied by a line total. Quantity <= 0 is treated as 1. */
export function hoursPerUnitFromLineTotal(lineTotal, quantity) {
  const total = Number(lineTotal);
  if (!Number.isFinite(total)) return 0;
  const qty = Number(quantity);
  const divisor = Number.isFinite(qty) && qty > 0 ? qty : 1;
  return total / divisor;
}

/** The line total implied by hours_per_unit. Quantity <= 0 is treated as 1. */
export function lineTotalFromHoursPerUnit(hoursPerUnit, quantity) {
  const hours = Number(hoursPerUnit);
  if (!Number.isFinite(hours)) return 0;
  const qty = Number(quantity);
  const multiplier = Number.isFinite(qty) && qty > 0 ? qty : 1;
  return hours * multiplier;
}

/** Trim float noise for display without rounding what gets stored. */
function trimHours(value, places = 4) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return String(Number(n.toFixed(places)));
}

export function OperationHoursCell({
  hoursPerUnit,
  lineQuantity,
  onChangeHoursPerUnit,
}) {
  const [basis, setBasis] = useState("unit");
  const perUnit = Number(hoursPerUnit) || 0;
  const lineTotal = lineTotalFromHoursPerUnit(perUnit, lineQuantity);
  const isTotal = basis === "total";

  return (
    <div className="flex flex-col items-center gap-0.5">
      <NumberInput
        align="center"
        step="0.1"
        min="0"
        value={isTotal ? trimHours(lineTotal) : hoursPerUnit}
        onValueChange={(v) =>
          onChangeHoursPerUnit(
            isTotal
              ? hoursPerUnitFromLineTotal(v ?? 0, lineQuantity)
              : (v ?? 0),
          )
        }
        title={isTotal ? "Hours for the whole line" : "Hours per unit"}
        aria-label={isTotal ? "Hours for the whole line" : "Hours per unit"}
        placeholder="hrs"
        className="h-8 text-[12px] px-1 w-full max-w-[60px] mx-auto"
      />
      <button
        type="button"
        onClick={() => setBasis(isTotal ? "unit" : "total")}
        title="Switch between hours per unit and hours for the whole line"
        className="wb-plain text-[9px] uppercase tracking-[0.3px] text-estimate-muted hover:text-violet-400 underline decoration-dotted"
      >
        {isTotal ? "line total" : "per unit"}
      </button>
      <span className="text-[9px] text-estimate-muted whitespace-nowrap">
        {isTotal
          ? `${trimHours(perUnit)} hr/unit`
          : `${trimHours(lineTotal)} hr line`}
      </span>
    </div>
  );
}

/**
 * Sub-sub-row shown under a sheet-unit material. Lets the user enter sheet
 * dimensions + rip width + kerf + rips-per-unit, and displays live
 * rips-per-sheet yield plus sheets-needed demand math.
 *
 * Millwork convention: a rip is a strip cut ALONG the length of the sheet
 * (parallel to the long edge) so each rip ends up `sheet_length` long. The
 * number of rips is therefore how many `(rip_width + kerf)` strips fit
 * ACROSS the sheet's WIDTH:
 *
 *     rips_per_sheet = floor(sheet_width / (rip_width + kerf))
 *
 * For a 48"W × 120"L sheet with 10" rips + 0.25" kerf:
 *     floor(48 / 10.25) = 4 rips, each 120" long.
 *
 * When the user also specifies `rips_per_unit` and the parent line item has
 * a quantity, we derive demand:
 *
 *     total_rips_needed = rips_per_unit × line_quantity
 *     sheets_needed     = ceil(total_rips_needed / rips_per_sheet)
 *
 * This is purely informational — `sheets_needed` is displayed as a nudge;
 * the authoritative sheet count driving cost remains the user-entered
 * `quantity_per_unit` on the parent component row.
 *
 * The server-side rip_cut result (in custom_component_results) overrides
 * this preview once Calculate runs, via the separate <YieldSubRow>.
 */
function RipCutRow({ component, update, lineQuantity }) {
  const sheetW = toFiniteNumber(component.dimensions?.width);
  const sheetL = toFiniteNumber(component.dimensions?.length);
  const ripW = toFiniteNumber(component.rip_cut?.rip_width);
  const kerf =
    toFiniteNumber(component.rip_cut?.kerf) ??
    (component.rip_cut?.kerf === 0 ? 0 : 0.25);
  const ripsPerUnit = toFiniteNumber(component.rip_cut?.rips_per_unit);
  const lineQty = toFiniteNumber(lineQuantity);

  const stride = (ripW ?? 0) + (kerf ?? 0.25);
  const rips =
    stride > 0 && sheetW != null && sheetW > 0
      ? Math.floor(sheetW / stride)
      : 0;
  const consumedWidth = rips > 0 ? stride * rips : 0;
  const hasAllInputs =
    sheetW != null && sheetW > 0 && sheetL != null && ripW != null && ripW > 0;

  // Demand math — only meaningful when user has entered rips-per-unit AND we
  // know the parent line's quantity AND the sheet actually yields rips.
  const totalRipsNeeded =
    ripsPerUnit != null && ripsPerUnit > 0 && lineQty != null && lineQty > 0
      ? ripsPerUnit * lineQty
      : null;
  const sheetsNeeded =
    totalRipsNeeded != null && rips > 0
      ? Math.ceil(totalRipsNeeded / rips)
      : null;

  const fields = [
    {
      label: "Sheet W″",
      step: "0.25",
      value: component.dimensions?.width ?? "",
      onChange: (v) =>
        update("dimensions", {
          ...(component.dimensions || {}),
          width: v ?? 0,
        }),
    },
    {
      label: "Sheet L″",
      step: "0.25",
      value: component.dimensions?.length ?? "",
      onChange: (v) =>
        update("dimensions", {
          ...(component.dimensions || {}),
          length: v ?? 0,
        }),
    },
    {
      label: "Rip W″",
      step: "0.25",
      value: component.rip_cut?.rip_width ?? "",
      onChange: (v) =>
        update("rip_cut", {
          ...(component.rip_cut || {}),
          rip_width: v ?? 0,
          kerf: component.rip_cut?.kerf ?? 0.25,
        }),
    },
    {
      label: "Kerf″",
      step: "0.125",
      value: component.rip_cut?.kerf ?? 0.25,
      onChange: (v) =>
        update("rip_cut", {
          ...(component.rip_cut || {}),
          kerf: v ?? 0,
        }),
    },
    {
      label: "Rips Req'd",
      step: "1",
      value: component.rip_cut?.rips_per_unit ?? "",
      title:
        "Rips required per assembly unit (multiplied by the line item's quantity to compute total rips needed)",
      onChange: (v) =>
        update("rip_cut", {
          ...(component.rip_cut || {}),
          rips_per_unit: v ?? 0,
        }),
    },
  ];

  return (
    <tr className="border-b border-estimate-border/30 text-[11px] text-estimate-muted shadow-[inset_3px_0_0_0_theme(colors.custom.DEFAULT)] bg-amber-500/[0.02]">
      <td></td>
      <td colSpan={5} className="py-1 px-2">
        <div className="flex gap-3 flex-wrap pl-2 items-end">
          {fields.map(({ label, step, value, onChange, title }) => (
            <label
              key={label}
              title={title}
              className="flex flex-col gap-0.5 uppercase tracking-[0.3px] text-[9px]"
            >
              {label}
              <NumberInput
                align="right"
                step={step}
                value={value}
                onValueChange={onChange}
                className="h-7 w-[68px] px-1 text-[11px]"
              />
            </label>
          ))}

          {/* Live yield indicator — computed client-side from formula */}
          {hasAllInputs && rips > 0 && (
            <div className="flex flex-col gap-0.5 pl-3 ml-1 border-l border-estimate-border/40 text-[10px] not-italic">
              <span className="uppercase tracking-[0.3px] text-estimate-muted text-[9px]">
                Yield
              </span>
              <span className="text-emerald-500 font-medium whitespace-nowrap">
                {rips} rips per sheet × {sheetL}″ long
              </span>
              <span className="text-estimate-muted whitespace-nowrap">
                (({ripW}″ + {kerf}″) × {rips} = {consumedWidth}″ of {sheetW}″
                width)
              </span>
            </div>
          )}
          {hasAllInputs && rips === 0 && (
            <div className="pl-3 ml-1 border-l border-estimate-border/40 text-rose-500 text-[10px] self-end whitespace-nowrap">
              Rip width + kerf exceeds {sheetW}″ sheet width
            </div>
          )}

          {/* Demand indicator — shows sheets needed when user enters
              rips-per-unit and the parent line has a quantity.
              Purely informational; does NOT write to quantity_per_unit. */}
          {sheetsNeeded != null && (
            <div className="flex flex-col gap-0.5 pl-3 ml-1 border-l border-estimate-border/40 text-[10px] not-italic">
              <span className="uppercase tracking-[0.3px] text-estimate-muted text-[9px]">
                Need
              </span>
              <span className="text-amber-400 font-medium whitespace-nowrap">
                {sheetsNeeded} sheet{sheetsNeeded === 1 ? "" : "s"} required
              </span>
              <span className="text-estimate-muted whitespace-nowrap">
                (⌈{totalRipsNeeded} rips ÷ {rips} per sheet⌉ = {sheetsNeeded})
              </span>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

/** Convert a possibly-string input value to a finite number or null. */
function toFiniteNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
